import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { usePagedResource } from "../api/usePagedResource";
import { useLiveCalls } from "../api/useLiveCalls";
import { DataTable } from "../components/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import type { Call, CallStatus } from "../api/types";
import { Card, PageHeader } from "../components/ui";
import { STATUS_LABELS, StatusBadge, isLive, useInterval } from "./Calls";

/**
 * Live es la vista de llamadas en curso.
 *
 * # Por qué es una pantalla propia y no un bloque del tablero
 *
 * Donde estaba —una lista de diez dentro del tablero— servía para echar un
 * vistazo, no para operar: sin acceso directo desde el menú, cortada a diez
 * filas y mezclada con indicadores del día que se miran una vez por la mañana.
 * Lo que se mira **mientras pasa** necesita su sitio, y quien vigila una
 * campaña no debería tener que recordar en qué pantalla estaba escondido.
 *
 * # Por qué el filtro va al servidor y no aquí
 *
 * «En curso» son cuatro estados, no uno. Filtrar en el navegador sobre la
 * primera página de llamadas —que es lo que hacía el tablero— deja fuera una
 * llamada larga en cuanto entran llamadas nuevas delante de ella: justo la
 * llamada que más interesa ver. El endpoint acepta la lista y pagina sobre el
 * resultado ya filtrado.
 */

// Los estados vivos salen de `isLive`, que es la definición que ya usa el
// resto del portal. Escribir la lista otra vez sería tener dos verdades que se
// separan en cuanto alguien añada un estado.
const LIVE_STATUSES = (Object.keys(STATUS_LABELS) as CallStatus[]).filter(isLive);
const LIVE_QUERY = `?status=${LIVE_STATUSES.join(",")}`;

// Con el canal en vivo el sondeo es red de seguridad; sin él, es lo único que
// trae novedades, y aquí se mira algo que cambia ahora mismo.
const POLL_MS = 5000;
const POLL_RED_MS = 30000;

export function Live() {
  const lista = usePagedResource<Call>(`/api/v1/calls${LIVE_QUERY}`);
  const calls = lista.data;
  const hayLlamadas = (calls ?? []).length > 0;

  // Igual que en la lista de llamadas: el evento trae el estado pero no el
  // sitio de la fila, así que se recarga en lugar de parchear. Y se agrupa en
  // una ventana corta porque una llamada que entra y contesta dispara varios
  // eventos seguidos.
  //
  // Aquí además el evento puede significar que una fila **se va**: una llamada
  // que termina deja de estar en curso y debe desaparecer de la vista. Por eso
  // se recarga con cualquier tipo de evento, no solo con los de alta.
  const recargaPendiente = useRef<number | null>(null);
  const { estado: canal, desfase } = useLiveCalls(
    useCallback(() => {
      if (recargaPendiente.current !== null) return;
      recargaPendiente.current = window.setTimeout(() => {
        recargaPendiente.current = null;
        void lista.reload(true);
      }, 400);
    }, [lista]),
  );
  useEffect(
    () => () => {
      if (recargaPendiente.current !== null) window.clearTimeout(recargaPendiente.current);
    },
    [],
  );

  useEffect(() => {
    if (desfase > 0) void lista.reload(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desfase]);

  useInterval(() => void lista.reload(true), canal === "en-vivo" ? POLL_RED_MS : POLL_MS);

  // El reloj avanza solo para que los contadores de duración corran sin
  // esperar a la siguiente recarga. Se detiene cuando no hay nada que contar:
  // repintar cada segundo una pantalla vacía es trabajo por nada.
  const [ahora, setAhora] = useState(() => Date.now());
  useInterval(() => setAhora(Date.now()), hayLlamadas ? 1000 : null);

  const navigate = useNavigate();

  const columnas = useMemo<ColumnDef<Call, any>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "Llamada",
        meta: { label: "Llamada" },
        cell: ({ row }) => (
          <Link
            to={`/calls/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
          >
            {row.original.id}
          </Link>
        ),
      },
      {
        id: "from",
        accessorKey: "from",
        header: "De",
        meta: { label: "De" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">{row.original.from}</span>
        ),
      },
      {
        id: "to",
        accessorKey: "to",
        header: "A",
        meta: { label: "A" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">{row.original.to}</span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado" },
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "queued_at",
        accessorKey: "queued_at",
        header: "En el sistema",
        meta: { label: "En el sistema" },
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums text-slate-600">
            {transcurrido(row.original.queued_at, ahora)}
          </span>
        ),
      },
      {
        id: "direction",
        accessorKey: "direction",
        header: "Dirección",
        meta: { label: "Dirección", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">
            {row.original.direction === "outbound" ? "Saliente" : "Entrante"}
          </span>
        ),
      },
    ],
    [ahora],
  );

  return (
    <div>
      <PageHeader
        title="En curso"
        description="Las llamadas que siguen vivas, según pasan. Para colgar una o ver su detalle, ábrela."
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-xs text-slate-500">
          {canal === "en-vivo" ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              En vivo: los cambios llegan solos.
            </>
          ) : lista.refrescando ? (
            "Actualizando…"
          ) : (
            `Sin canal en vivo; se actualiza cada ${POLL_MS / 1000} s.`
          )}
        </p>
        {calls !== null && (
          <p className="text-xs text-slate-500">
            {calls.length === 0
              ? "Ninguna en curso"
              : `${calls.length} en curso${lista.hayMas ? " en esta página" : ""}`}
          </p>
        )}
      </div>

      <DataTable<Call>
        data={calls}
        columns={columnas}
        getRowId={(call) => call.id}
        loading={lista.loading}
        error={lista.error}
        onRetry={() => void lista.reload(false)}
        caption="Llamadas en curso"
        empty={{
          title: "Ninguna llamada en curso",
          description:
            "Aquí aparecen las llamadas en cola, originadas, timbrando o conversando, sin que haga falta recargar.",
        }}
        search={{ placeholder: "Buscar por número o id…" }}
        hideableColumns
        urlStateKey="en-curso"
        onRowClick={(call) => navigate(`/calls/${call.id}`)}
        pagination={{
          pageSize: lista.enEstaPagina,
          hasNext: lista.hayMas,
          hasPrevious: lista.hayAnterior,
          onNext: () => lista.siguiente(),
          onPrevious: () => lista.anterior(),
        }}
      />

      <Card className="mt-4 p-4 text-xs text-slate-500">
        <strong className="font-medium text-slate-400">Qué cuenta «En el sistema»:</strong> el
        tiempo desde que la llamada entró a la plataforma, no el de conversación. Incluye lo que
        tarda el carrier en encaminarla y lo que suena antes de contestar. Los segundos
        conversados y facturados salen en el detalle de la llamada, cuando termina.
      </Card>
    </div>
  );
}

/**
 * transcurrido da el tiempo desde `iso` en mm:ss, o h:mm:ss pasada la hora.
 *
 * Un futuro —un reloj del servidor por delante del navegador— se enseña como
 * cero en lugar de un negativo: la diferencia entre dos relojes no es
 * información que nadie pueda usar, y un «-00:02» parece un error de la
 * plataforma.
 */
export function transcurrido(iso: string, ahora: number): string {
  const inicio = new Date(iso).getTime();
  if (Number.isNaN(inicio)) return "—";
  const segundos = Math.max(0, Math.floor((ahora - inicio) / 1000));
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;
  const dosDigitos = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${dosDigitos(m)}:${dosDigitos(s)}` : `${dosDigitos(m)}:${dosDigitos(s)}`;
}
